import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import supabase from '../services/supabase'
import Newsletter from '../components/Newsletter'
import Faq from '../components/Faq'
import SchemaMarkup from '../components/SchemaMarkup'
import CanonicalUrl from '../components/CanonicalUrl'
import StatusCode from '../components/StatusCode'

const IaeDetailsPage = () => {
  const { cod_iae, slug_iae } = useParams()
  const [iaeDetails, setIaeDetails] = useState(null)
  const [sectionTitle, setSectionTitle] = useState('')
  const [associatedCnaeCodes, setAssociatedCnaeCodes] = useState([])
  const [relatedIaeCodes, setRelatedIaeCodes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchIaeDetails = async () => {
      try {
        setIsLoading(true)
        setError(null)

        let iaeData = null
        let iaeError = null

        console.log('Trying to fetch IAE by slug:', slug_iae)

        // First try to fetch by slug
        const slugResult = await supabase.rpc('get_iae_details_by_slug', {
          input_slug: slug_iae,
        })

        if (slugResult.data && slugResult.data.length > 0) {
          iaeData = slugResult.data
          iaeError = null
        } else {
          // If no results by slug, try to fetch by code
          console.log('No results by slug, trying to fetch by code:', cod_iae)

          const codeResult = await supabase.rpc('get_iae_details_by_code', {
            iae_code: cod_iae,
          })

          iaeData = codeResult.data
          iaeError = codeResult.error
        }

        if (iaeError) {
          console.error('Error fetching IAE details:', iaeError)
        }

        if (!iaeData || iaeData.length === 0) {
          throw new Error('No se encontró el código IAE')
        }

        setIaeDetails(iaeData[0])

        // Fetch section title
        if (iaeData[0].cod_integro_iae) {
          const { data: sectionData, error: sectionError } = await supabase.rpc(
            'get_iae_section_title_by_code',
            { codigo_integro_iae: iaeData[0].cod_integro_iae }
          )

          if (!sectionError && sectionData && sectionData.length > 0) {
            setSectionTitle(sectionData[0].titulo_seccion)
          } else {
            console.error('Error fetching section title:', sectionError)
          }
        }

        // Process related IAE codes
        if (iaeData[0].relaciones_iae && iaeData[0].relaciones_iae.length > 0) {
          // Get slug for each related IAE code
          const relatedCodesWithSlugs = await Promise.all(
            iaeData[0].relaciones_iae.map(async (code) => {
              // Get IAE slug by code
              const { data: slugData } = await supabase.rpc(
                'get_slug_iae_by_code',
                {
                  input_iae_code: code,
                }
              )

              return {
                code,
                slug:
                  slugData && slugData.length > 0 ? slugData[0].slug_iae : code,
              }
            })
          )

          setRelatedIaeCodes(relatedCodesWithSlugs)
        } else {
          setRelatedIaeCodes([])
        }

        // Fetch associated CNAE codes using the IAE slug
        const { data: cnaeCodesData, error: cnaeCodesError } =
          await supabase.rpc('get_cnae_associated_to_iae_by_slug', {
            iae_slug: slug_iae,
          })

        if (cnaeCodesError) {
          console.error('Error fetching associated CNAE codes:', cnaeCodesError)
          setAssociatedCnaeCodes([])
        } else if (cnaeCodesData && cnaeCodesData.length > 0) {
          // Format the CNAE codes with their slugs
          const formattedCnaeCodes = cnaeCodesData.map((item) => ({
            code: item.codigo_cnae_asociado,
            slug: item.slug_cnae,
          }))

          setAssociatedCnaeCodes(formattedCnaeCodes)
        } else {
          setAssociatedCnaeCodes([])
        }

        // Search counter is now incremented in SearchBar.js when a code is selected
      } catch (error) {
        console.error('Error fetching IAE details:', error)
        setError(error.message || 'Error al cargar los detalles del código IAE')
      } finally {
        setIsLoading(false)
      }
    }

    if (slug_iae && cod_iae) {
      fetchIaeDetails()
    }
  }, [slug_iae, cod_iae])

  // Format description text with proper line breaks
  const formatText = (text) => {
    if (!text) return null

    // Split by line breaks and filter out empty lines
    const paragraphs = text.split('\n').filter((line) => line.trim() !== '')

    return paragraphs.map((paragraph, index) => (
      <p key={index} className='mb-3'>
        {paragraph}
      </p>
    ))
  }

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return `${date.getDate().toString().padStart(2, '0')}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`
  }

  return (
    <div className='container'>
      {isLoading ? (
        <div className='loading'>
          <div className='loading-spinner'></div>
        </div>
      ) : error ? (
        <>
          <StatusCode code={404} />
          <div className='error-message'>{error}</div>
        </>
      ) : (
        <>
          {/* Canonical URL */}
          <CanonicalUrl
            url={`https://conversoriaecnae.es/iae/${iaeDetails.cod_iae.replace('.', '')}/${iaeDetails.slug_iae}`}
          />

          {/* Dataset Schema */}
          <SchemaMarkup
            type='Dataset'
            data={{
              name: `Epígrafe IAE: ${iaeDetails.cod_iae} - ${iaeDetails.titulo_iae}`,
              description: iaeDetails.descripcion_iae,
              keywords: iaeDetails.palabras_claves_iae || [],
              url: `https://conversoriaecnae.es/iae/${iaeDetails.cod_iae.replace(
                '.',
                ''
              )}/${iaeDetails.slug_iae}`,
              identifier: iaeDetails.cod_iae,
              creator: 'Conversor IAE CNAE',
              datePublished: iaeDetails.ultima_actualizacion_iae,
              dateModified: iaeDetails.ultima_actualizacion_iae,
            }}
          />
          <div className='bg-surface rounded-lg p-6 my-6'>
            <div className='mb-4'>
              <span className='bg-iae text-background text-xs font-bold px-2 py-1 rounded'>
                IAE
              </span>
            </div>

            <h1 className='text-xl font-bold mb-1'>{iaeDetails.cod_iae}</h1>
            <h2 className='text-xl font-semibold mb-6'>
              {iaeDetails.titulo_iae}
            </h2>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Sección</h3>
              <p className='text-text-primary'>{sectionTitle}</p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Código íntegro</h3>
              <p className='text-text-primary'>{iaeDetails.cod_integro_iae}</p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Descripción</h3>
              <div className='text-text-primary'>
                {formatText(iaeDetails.descripcion_iae)}
              </div>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Alta IAE</h3>
              <p className='text-text-primary'>
                {iaeDetails.alta_iae ? 'Sí' : 'No'}
              </p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Categoría</h3>
              <p className='text-text-primary'>{iaeDetails.categoria_iae}</p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Nivel</h3>
              <p className='text-text-primary'>{iaeDetails.nivel_iae}</p>
            </div>

            {iaeDetails.palabras_claves_iae &&
              iaeDetails.palabras_claves_iae.length > 0 && (
                <div className='mb-4'>
                  <h3 className='text-primary font-bold mb-1'>
                    Palabras claves
                  </h3>
                  <div className='flex flex-wrap gap-2'>
                    {iaeDetails.palabras_claves_iae.map((keyword, index) => (
                      <span
                        key={index}
                        className='bg-card px-2 py-1 rounded text-sm'
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            {relatedIaeCodes.length > 0 && (
              <div className='mb-4'>
                <h3 className='text-primary font-bold mb-1'>
                  Códigos IAE relacionados
                </h3>
                <div className='flex flex-wrap gap-2'>
                  {relatedIaeCodes.map((code, index) => (
                    <Link
                      key={index}
                      to={`/iae/${code.code.replace('.', '')}/${code.slug}`}
                      className='text-iae hover:underline'
                    >
                      {code.code}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>
                Códigos CNAE asociados
              </h3>
              {associatedCnaeCodes.length > 0 ? (
                <div className='flex flex-wrap gap-2'>
                  {associatedCnaeCodes.map((code, index) => (
                    <Link
                      key={index}
                      to={`/cnae-2025/${code.code.replace('.', '')}/${
                        code.slug
                      }`}
                      className='text-cnae hover:underline'
                    >
                      {code.code}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-text-secondary'>
                  No se encontraron códigos CNAE asociados
                </p>
              )}
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Fuente</h3>
              <p className='text-text-primary'>{iaeDetails.fuente_iae}</p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>
                Última actualización
              </h3>
              <p className='text-text-primary'>
                {formatDate(iaeDetails.ultima_actualizacion_iae)}
              </p>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className='mt-12'>
            <Newsletter />
          </div>

          {/* FAQ Section */}
          <Faq
            title='Preguntas Frecuentes sobre Epígrafes IAE (FAQ)'
            description='Resolvemos tus dudas sobre los epígrafes IAE, su aplicación y cómo utilizarlos correctamente para tu actividad económica. Información actualizada según las últimas disposiciones oficiales.'
            faqData={[
              {
                question: '¿Qué es el IAE?',
                answer:
                  'El IAE (Impuesto de Actividades Económicas) es un tributo que deben pagar las empresas y autónomos que realizan una actividad económica en España. Este impuesto se calcula en función de los ingresos y la actividad que desarrolle cada empresa o autónomo, y se clasifica mediante un código único llamado código IAE, que permite identificar la actividad económica específica.',
              },
              {
                question:
                  '¿Cómo encontrar el código IAE de mi actividad económica?',
                answer:
                  'Para encontrar el código IAE correspondiente a tu actividad económica, puedes utilizar nuestro conversor de códigos IAE. Simplemente busca la actividad que realizas en el buscador y te mostraremos el código IAE relacionado, junto con una descripción detallada de la actividad. Además, si necesitas convertirlo a un código CNAE, también lo podrás hacer de forma rápida y sencilla.',
              },
              {
                question:
                  '¿Cuál es la diferencia entre los códigos IAE y CNAE?',
                answer:
                  'El IAE es un código utilizado en España para clasificar las actividades económicas a efectos fiscales, mientras que el CNAE (Clasificación Nacional de Actividades Económicas) es utilizado por el INE (Instituto Nacional de Estadística) para estadísticas nacionales y en algunos trámites administrativos. Aunque ambos sistemas clasifican actividades económicas, el IAE se enfoca más en el ámbito fiscal y el CNAE tiene un alcance más amplio.',
              },
              {
                question: '¿Cómo puedo saber si debo pagar el IAE?',
                answer:
                  'El IAE debe ser pagado por las empresas y autónomos cuyo volumen de negocio supere el umbral establecido por la ley. Existen algunas exenciones, como para aquellos que facturan por debajo de un umbral específico o si la actividad no está sujeta a este impuesto. Para saber si estás obligado a pagar el IAE, consulta con un asesor fiscal o revisa la normativa vigente en España.',
              },
              {
                question:
                  '¿Puedo cambiar mi código IAE si cambio de actividad económica?',
                answer:
                  'Sí, si decides cambiar tu actividad económica, deberás actualizar tu código IAE en la Agencia Tributaria. Es importante que notifiques cualquier cambio en tu actividad para que se refleje correctamente en tus declaraciones fiscales y para evitar problemas futuros con el fisco.',
              },
              {
                question:
                  '¿Es necesario un código IAE para todos los negocios en España?',
                answer:
                  'Sí, el código IAE es necesario para todos los negocios y actividades económicas que estén registrados en el sistema fiscal español, ya sea una actividad industrial, comercial o de servicios. Sin embargo, existen algunas excepciones y exenciones, por lo que es recomendable consultar a un asesor fiscal o revisar la normativa vigente para determinar si tu actividad está obligada a tener un código IAE.',
              },
              {
                question: '¿Qué es el certificado IAE?',
                answer:
                  'El certificado IAE es un documento oficial que acredita que una empresa o autónomo está inscrito en el registro del Impuesto de Actividades Económicas (IAE). Este certificado puede ser requerido en diversos trámites administrativos o para formalizar la actividad económica de un negocio ante organismos públicos o privados. En él, se detallan los códigos IAE asociados a la actividad económica que realiza el contribuyente, así como su alta en el sistema tributario.',
              },
              {
                question: '¿Cómo puedo obtener el certificado IAE?',
                answer:
                  'Para obtener el certificado IAE, debes solicitarlo a través de la Agencia Tributaria. El proceso se realiza de manera online, accediendo a tu sede electrónica y utilizando tu certificado digital o cl@ve. También puedes realizar la solicitud en persona en las oficinas de la Agencia Tributaria. Una vez que se emita, recibirás el certificado con los códigos IAE correspondientes a tu actividad económica registrada.',
              },
            ]}
          />
        </>
      )}
    </div>
  )
}

export default IaeDetailsPage
